import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2, Truck, Store } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";

type PendingReview = {
  orderId: string;
  orderRef: string;
  deliveredAt: string;
  needsSellerReview: boolean;
  needsDriverReview: boolean;
  sellerName: string | null;
  sellerId: string | null;
  driverName: string | null;
  driverId: string | null;
  deliveryRequestId: string | null;
};

const DISMISS_KEY = "marketlink.reviewBanner.dismissedUntil";
const DISMISS_DURATION_MS = 60 * 60 * 1000; // 1 hour, then it's back

function loadDismissedUntil(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
}

function setDismissedUntil(ts: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DISMISS_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

function StarPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= value;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="rounded-full p-1 transition hover:bg-muted"
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
          >
            <Star
              className={`h-7 w-7 ${
                filled ? "fill-amber-400 text-amber-500" : "text-muted-foreground"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export function ReviewPromptBanner() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data } = useQuery<{ items: PendingReview[] }>({
    queryKey: ["/api/my/pending-reviews"],
    enabled: isAuthenticated,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  const [dismissedUntil, setDismissedUntilState] = useState<number>(() => loadDismissedUntil());
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<PendingReview | null>(null);
  const [sellerRating, setSellerRating] = useState(0);
  const [sellerNote, setSellerNote] = useState("");
  const [driverRating, setDriverRating] = useState(0);
  const [driverNote, setDriverNote] = useState("");

  const items = useMemo(() => data?.items ?? [], [data]);
  const next = items[0] ?? null;

  // Auto-open the modal whenever there's a pending review and the user
  // hasn't tapped the tiny "later" link recently. Reappears after the
  // dismiss window expires.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!next) return;
    if (Date.now() < dismissedUntil) return;
    setActive(next);
    setSellerRating(0);
    setSellerNote("");
    setDriverRating(0);
    setDriverNote("");
    setOpen(true);
  }, [isAuthenticated, next?.orderId, dismissedUntil]);

  const submitSeller = useMutation({
    mutationFn: async (review: PendingReview) => {
      const res = await apiRequest("POST", "/api/reviews", {
        orderId: review.orderId,
        rating: sellerRating,
        body: sellerNote.trim() || undefined,
      });
      return res.json();
    },
  });

  const submitDriver = useMutation({
    mutationFn: async (review: PendingReview) => {
      const res = await apiRequest("POST", "/api/driver-reviews", {
        orderId: review.orderId,
        rating: driverRating,
        body: driverNote.trim() || undefined,
      });
      return res.json();
    },
  });

  async function handleSubmit() {
    if (!active) return;
    try {
      if (active.needsSellerReview && sellerRating > 0) {
        await submitSeller.mutateAsync(active);
      }
      if (active.needsDriverReview && driverRating > 0) {
        await submitDriver.mutateAsync(active);
      }
      toast({ title: "Thanks for the review" });
      queryClient.invalidateQueries({ queryKey: ["/api/my/pending-reviews"] });
      setOpen(false);
    } catch (err: any) {
      toast({
        title: "Couldn't submit review",
        description: err?.message || "Try again in a moment.",
        variant: "destructive",
      });
    }
  }

  function handleSkip() {
    const next = Date.now() + DISMISS_DURATION_MS;
    setDismissedUntil(next);
    setDismissedUntilState(next);
    setOpen(false);
  }

  if (!isAuthenticated || !active) return null;

  const submitting = submitSeller.isPending || submitDriver.isPending;
  const canSubmit =
    (active.needsSellerReview ? sellerRating > 0 : true) &&
    (active.needsDriverReview ? driverRating > 0 : true) &&
    (sellerRating > 0 || driverRating > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleSkip()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rate your order #{active.orderRef}</DialogTitle>
          <DialogDescription>
            Your honest reviews help neighbors find the best local sellers and
            drivers. Takes 10 seconds.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {active.needsSellerReview && (
            <div className="space-y-2 rounded-xl border p-3">
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">
                  Seller — {active.sellerName ?? "your seller"}
                </span>
              </div>
              <StarPicker value={sellerRating} onChange={setSellerRating} />
              <Textarea
                value={sellerNote}
                onChange={(e) => setSellerNote(e.target.value)}
                placeholder="What stood out? (optional)"
                className="min-h-[64px] text-sm"
                maxLength={5000}
              />
            </div>
          )}

          {active.needsDriverReview && (
            <div className="space-y-2 rounded-xl border p-3">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-brand-secondary" />
                <span className="text-sm font-semibold">
                  Driver — {active.driverName ?? "your driver"}
                </span>
              </div>
              <StarPicker value={driverRating} onChange={setDriverRating} />
              <Textarea
                value={driverNote}
                onChange={(e) => setDriverNote(e.target.value)}
                placeholder="How was the handoff? (optional)"
                className="min-h-[64px] text-sm"
                maxLength={5000}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="w-full"
            size="lg"
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Submit review
          </Button>
          {/* Skip is intentionally tiny + buried below the primary CTA so
              the buyer feels nudged to actually leave a review. Comes
              back after DISMISS_DURATION_MS regardless. */}
          <button
            type="button"
            onClick={handleSkip}
            className="self-center text-[11px] text-muted-foreground/60 underline-offset-2 hover:text-muted-foreground hover:underline"
          >
            maybe later
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
